import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  applyFilterPatch,
  clearFilterParams,
  fromSavedFilter,
  readFilterValues,
  sameFilters,
  toSavedFilter,
} from '@bbpos/ui';
import type { FilterDateKeys, FilterValues, SavedFiltersPanelProps } from '@bbpos/ui';
import type { SavedFilter, SavedFilterPage } from '@bbpos/types';
import { useAuthStore } from '@/stores/use-auth-store';
import { getLocationTimezone } from '@/lib/tenant-clock';
import { savedFiltersService } from '@/services/saved-filters.service';

/** Owner decision (T27, Q17): MANAGER and OWNER share with the team; anyone saves a private filter. */
const TEAM_SHARE_ROLES = ['OWNER', 'MANAGER'];

export interface PosListFilterBarConfig {
  /** The list — admin Bills and the POS Bill history share `bills`. */
  page: SavedFilterPage;
  /** The list's filter parameters in the URL — the endpoint's own names. */
  keys: string[];
  date?: FilterDateKeys;
  /** The filter holding the store whose clock the dates are on. */
  locationKey?: string;
  /** No favourites requests while the till is offline. */
  online: boolean;
}

/**
 * The shared filter bar's state on the till (T27): filters in the URL,
 * favourites on the server, a preset range saved as a period, and the user's
 * default favourite applied when the page opens with no filter of its own.
 * Days are the store's (T18): the filtered store's zone, else the business's
 * — the zone the API filters the list on.
 */
export function usePosListFilterBar(config: PosListFilterBarConfig) {
  const { page, keys, date, locationKey, online } = config;
  const [params, setParams] = useSearchParams();
  const role = useAuthStore((s) => s.profile?.role ?? '');

  const values = useMemo(() => readFilterValues(params, keys), [params, keys]);
  const zoneFor = useCallback(
    (v: Record<string, string>) => getLocationTimezone((locationKey && v[locationKey]) || null),
    [locationKey],
  );
  const timeZone = zoneFor(values);

  const onChange = useCallback(
    (patch: FilterValues) => setParams((prev) => applyFilterPatch(prev, patch), { replace: true }),
    [setParams],
  );
  const onReset = useCallback(
    () => setParams((prev) => clearFilterParams(prev, keys)),
    [setParams, keys],
  );

  const [filters, setFilters] = useState<SavedFilter[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setFilters(await savedFiltersService.list(page));
    } catch {
      setFilters((prev) => prev ?? []);
    }
  }, [page]);

  useEffect(() => {
    if (online) void reload();
  }, [online, reload]);

  // Only the keys this screen knows: a `bills` favourite saved in the admin may
  // carry filters the till does not show (e.g. one customer's bills).
  const apply = useCallback(
    (f: Pick<SavedFilter, 'filters'>, replace = false) => {
      const opened = fromSavedFilter(f.filters, date, zoneFor(f.filters));
      const known = Object.fromEntries(Object.entries(opened).filter(([k]) => keys.includes(k)));
      setParams((prev) => applyFilterPatch(clearFilterParams(prev, keys), known), { replace });
    },
    [date, zoneFor, setParams, keys],
  );

  // The user's default favourite, once, when the page opens without filters of
  // its own — a link that carries filters wins.
  const defaultChecked = useRef(false);
  useEffect(() => {
    if (defaultChecked.current || !filters) return;
    defaultChecked.current = true;
    const def = filters.find((f) => f.is_default && f.is_mine);
    if (def && Object.keys(values).length === 0) apply(def, true);
  }, [filters, values, apply]);

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the filter');
    }
  };

  const current = toSavedFilter(values, date, timeZone);
  const list = filters ?? [];
  const active = list.find((f) => sameFilters(f.filters, current));

  const favorites: SavedFiltersPanelProps = {
    filters: list,
    activeId: active?.id ?? null,
    canShare: TEAM_SHARE_ROLES.includes(role),
    canSave: Object.keys(current).length > 0,
    saving,
    error,
    onApply: (item) => {
      const f = list.find((x) => x.id === item.id);
      if (f) apply(f);
    },
    onSave: ({ name, isShared, isDefault }) => {
      setSaving(true);
      void run(() =>
        savedFiltersService.create({
          page,
          name,
          filters: current,
          is_shared: isShared,
          is_default: isDefault,
        }),
      ).finally(() => setSaving(false));
    },
    onDelete: (item) => void run(() => savedFiltersService.remove(item.id)),
    onToggleDefault: (item) =>
      void run(() => savedFiltersService.update(item.id, { is_default: !item.is_default })),
    onToggleShare: (item) =>
      void run(() => savedFiltersService.update(item.id, { is_shared: !item.is_shared })),
  };

  return { values, onChange, onReset, timeZone, favorites };
}
